import { useCallback, useEffect, useRef, useState } from 'react';

const INPUT_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;

function downsampleFloat32ToInt16(float32Data, inputSampleRate, outputSampleRate) {
  if (outputSampleRate >= inputSampleRate) {
    const sameRate = new Int16Array(float32Data.length);
    for (let i = 0; i < float32Data.length; i += 1) {
      const s = Math.max(-1, Math.min(1, float32Data[i]));
      sameRate[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return sameRate;
  }

  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.max(1, Math.round(float32Data.length / ratio));
  const result = new Int16Array(outputLength);
  let inputOffset = 0;

  for (let i = 0; i < outputLength; i += 1) {
    const nextInputOffset = Math.round((i + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let j = inputOffset; j < nextInputOffset && j < float32Data.length; j += 1) {
      accum += float32Data[j];
      count += 1;
    }
    const sample = count > 0 ? accum / count : 0;
    const s = Math.max(-1, Math.min(1, sample));
    result[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    inputOffset = nextInputOffset;
  }

  return result;
}

function int16ToFloat32(buffer) {
  const int16 = new Int16Array(buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    float32[i] = int16[i] / 0x8000;
  }
  return float32;
}

export function useVoiceAgent({ getSocketUrl, onConversationMessage, onVoiceError, onToolEvent }) {
  const [listening, setListening] = useState(false);
  const [speaker, setSpeaker] = useState('idle');
  const [micLevel, setMicLevel] = useState(0.1);

  const wsRef = useRef(null);
  const inputAudioContextRef = useRef(null);
  const outputAudioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const processorRef = useRef(null);
  const sinkNodeRef = useRef(null);
  const activeSourcesRef = useRef(new Set());
  const playbackEndRef = useRef(0);
  const isStartingRef = useRef(false);
  const lastConversationRef = useRef('');

  const conversationHandlerRef = useRef(onConversationMessage);
  const errorHandlerRef = useRef(onVoiceError);
  const toolEventHandlerRef = useRef(onToolEvent);
  const transportErroredRef = useRef(false);
  const serverMessageSeenRef = useRef(false);
  const explicitErrorRef = useRef('');

  useEffect(() => {
    conversationHandlerRef.current = onConversationMessage;
    errorHandlerRef.current = onVoiceError;
    toolEventHandlerRef.current = onToolEvent;
  }, [onConversationMessage, onVoiceError, onToolEvent]);

  function emitVoiceError(message) {
    explicitErrorRef.current = message;
    if (errorHandlerRef.current) {
      errorHandlerRef.current(message);
    }
  }

  function stopPlayback() {
    activeSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch (_) {
        // no-op
      }
    });
    activeSourcesRef.current.clear();
    playbackEndRef.current = 0;
  }

  const closeAudioResources = useCallback(async () => {
    stopPlayback();

    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch (_) {
        // no-op
      }
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }

    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch (_) {
        // no-op
      }
      sourceNodeRef.current = null;
    }

    if (sinkNodeRef.current) {
      try {
        sinkNodeRef.current.disconnect();
      } catch (_) {
        // no-op
      }
      sinkNodeRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (inputAudioContextRef.current) {
      try {
        await inputAudioContextRef.current.close();
      } catch (_) {
        // no-op
      }
      inputAudioContextRef.current = null;
    }

    if (outputAudioContextRef.current) {
      try {
        await outputAudioContextRef.current.close();
      } catch (_) {
        // no-op
      }
      outputAudioContextRef.current = null;
    }
  }, []);

  const stopVoiceCapture = useCallback(async () => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close(1000, 'voice_session_end');
    }

    await closeAudioResources();
    setListening(false);
    setSpeaker('idle');
    setMicLevel(0.1);
    isStartingRef.current = false;
    transportErroredRef.current = false;
    serverMessageSeenRef.current = false;
    explicitErrorRef.current = '';
  }, [closeAudioResources]);

  async function playPcmChunk(arrayBuffer) {
    if (!arrayBuffer || arrayBuffer.byteLength < 2) return;

    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) return;

      let outputContext = outputAudioContextRef.current;
      if (!outputContext) {
        outputContext = new AudioContextCtor();
        outputAudioContextRef.current = outputContext;
      }
      if (outputContext.state === 'suspended') {
        await outputContext.resume();
      }

      const samples = int16ToFloat32(arrayBuffer);
      const audioBuffer = outputContext.createBuffer(1, samples.length, OUTPUT_SAMPLE_RATE);
      audioBuffer.copyToChannel(samples, 0);

      const source = outputContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(outputContext.destination);

      const startAt = Math.max(outputContext.currentTime + 0.005, playbackEndRef.current);
      source.start(startAt);
      playbackEndRef.current = startAt + audioBuffer.duration;
      activeSourcesRef.current.add(source);
      source.onended = () => {
        activeSourcesRef.current.delete(source);
        if (activeSourcesRef.current.size === 0) {
          setSpeaker('idle');
        }
      };
    } catch (error) {
      emitVoiceError('Failed playing voice audio: ' + error.message);
    }
  }

  function handleServerEvent(payload) {
    const eventType = payload?.type || '';
    if (!eventType) return;

    if (eventType === 'Error') {
      emitVoiceError(payload.message || payload.description || 'Voice session error.');
      return;
    }

    if (eventType === 'ToolCallStart') {
      if (toolEventHandlerRef.current) {
        toolEventHandlerRef.current({
          phase: 'start',
          name: String(payload.name || ''),
          action: String(payload.action || payload.name || 'Processing...'),
          arguments: payload.arguments && typeof payload.arguments === 'object' ? payload.arguments : {}
        });
      }
      return;
    }

    if (eventType === 'ToolCallResult') {
      if (toolEventHandlerRef.current) {
        toolEventHandlerRef.current({
          phase: 'result',
          name: String(payload.name || ''),
          ok: Boolean(payload.ok)
        });
      }
      return;
    }

    if (eventType === 'FunctionCallRequest') {
      const functions = Array.isArray(payload.functions) ? payload.functions : [];
      if (toolEventHandlerRef.current) {
        functions.forEach((entry) => {
          const args = entry?.arguments;
          let parsedArgs = {};
          if (args && typeof args === 'object') {
            parsedArgs = args;
          } else if (typeof args === 'string') {
            try {
              parsedArgs = JSON.parse(args);
            } catch (_) {
              parsedArgs = {};
            }
          }
          toolEventHandlerRef.current({
            phase: 'start',
            name: String(entry?.name || ''),
            action: String(entry?.name || 'Processing...'),
            arguments: parsedArgs
          });
        });
      }
      return;
    }

    if (eventType === 'ConversationText') {
      const role = payload.role === 'assistant' ? 'assistant' : 'user';
      const content = String(payload.content || '').trim();
      if (!content) return;
      const signature = `${role}:${content}`;
      if (lastConversationRef.current === signature) return;
      lastConversationRef.current = signature;
      if (conversationHandlerRef.current) {
        conversationHandlerRef.current({ role, content });
      }
      if (toolEventHandlerRef.current) {
        toolEventHandlerRef.current({ phase: 'clear' });
      }
      return;
    }

    if (eventType === 'UserStartedSpeaking') {
      setSpeaker('user');
      stopPlayback();
      if (toolEventHandlerRef.current) {
        toolEventHandlerRef.current({ phase: 'clear' });
      }
      return;
    }

    if (eventType === 'AgentThinking' || eventType === 'AgentStartedSpeaking' || eventType === 'AgentProcessing') {
      setSpeaker('sigmund');
      if (eventType === 'AgentStartedSpeaking' && toolEventHandlerRef.current) {
        toolEventHandlerRef.current({ phase: 'clear' });
      }
      return;
    }

    if (eventType === 'AgentAudioDone' || eventType === 'AgentDoneSpeaking') {
      setSpeaker('idle');
    }
  }

  async function startVoiceCapture({ companyUid, authToken }) {
    if (isStartingRef.current || listening) return;
    if (!companyUid || !authToken || !getSocketUrl) {
      emitVoiceError('Voice session requires authenticated company context.');
      return;
    }

    isStartingRef.current = true;

    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) {
        throw new Error('Web Audio is not supported on this browser.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const inputContext = new AudioContextCtor();
      inputAudioContextRef.current = inputContext;
      if (inputContext.state === 'suspended') {
        await inputContext.resume();
      }

      const source = inputContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      const processor = inputContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      const sinkGain = inputContext.createGain();
      sinkGain.gain.value = 0;
      sinkNodeRef.current = sinkGain;

      source.connect(processor);
      processor.connect(sinkGain);
      sinkGain.connect(inputContext.destination);

      const socketUrl = getSocketUrl(companyUid, authToken);
      const ws = new WebSocket(socketUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      transportErroredRef.current = false;
      serverMessageSeenRef.current = false;
      explicitErrorRef.current = '';

      ws.onopen = () => {
        setListening(true);
        setSpeaker('idle');
      };

      ws.onmessage = async (event) => {
        serverMessageSeenRef.current = true;
        if (typeof event.data === 'string') {
          try {
            handleServerEvent(JSON.parse(event.data));
          } catch (_) {
            // Ignore non-JSON control messages.
          }
          return;
        }

        if (event.data instanceof ArrayBuffer) {
          setSpeaker('sigmund');
          await playPcmChunk(event.data);
          return;
        }

        if (event.data instanceof Blob) {
          setSpeaker('sigmund');
          const buffer = await event.data.arrayBuffer();
          await playPcmChunk(buffer);
        }
      };

      ws.onerror = () => {
        transportErroredRef.current = true;
      };

      ws.onclose = async (closeEvent) => {
        if (
          closeEvent.code !== 1000 &&
          !explicitErrorRef.current
        ) {
          if (closeEvent.code === 4401) {
            emitVoiceError('Voice authentication failed. Please log out and log in again.');
          } else if (closeEvent.code === 4403) {
            emitVoiceError('Voice access denied for this company.');
          } else if (closeEvent.code === 1006 || transportErroredRef.current) {
            emitVoiceError(
              'Voice websocket failed. Backend voice route may be unavailable, or websocket proxy/upgrade is not configured.'
            );
          } else {
            const reason = closeEvent.reason ? ` (${closeEvent.reason})` : '';
            emitVoiceError(`Voice connection closed with code ${closeEvent.code}${reason}.`);
          }
        }

        await closeAudioResources();
        setListening(false);
        setSpeaker('idle');
        setMicLevel(0.1);
        wsRef.current = null;
        transportErroredRef.current = false;
        serverMessageSeenRef.current = false;
        explicitErrorRef.current = '';
      };

      processor.onaudioprocess = (audioEvent) => {
        const wsCurrent = wsRef.current;
        if (!wsCurrent || wsCurrent.readyState !== WebSocket.OPEN) return;

        const channel = audioEvent.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < channel.length; i += 1) {
          sum += channel[i] * channel[i];
        }
        const rms = Math.sqrt(sum / channel.length);
        setMicLevel(Math.max(0.05, Math.min(1, rms * 5)));

        const downsampled = downsampleFloat32ToInt16(channel, inputContext.sampleRate, INPUT_SAMPLE_RATE);
        wsCurrent.send(downsampled.buffer);
      };
    } catch (error) {
      emitVoiceError('Unable to start voice session: ' + error.message);
      await stopVoiceCapture();
    } finally {
      isStartingRef.current = false;
    }
  }

  useEffect(() => {
    return () => {
      stopVoiceCapture();
    };
  }, [stopVoiceCapture]);

  return {
    listening,
    speaker,
    micLevel,
    startVoiceCapture,
    stopVoiceCapture
  };
}
